import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const GoalTracker = ({ goals, loading = false }) => {
  const calculateProgress = (current, target) => {
    return Math.min((current / target) * 100, 100);
  };

  const calculateDaysRemaining = (targetDate) => {
    const today = new Date('2026-02-17');
    const target = new Date(targetDate);
    const diffTime = target - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="bg-card rounded-2xl p-4 md:p-6 shadow-elevation-2">
        <div className="flex items-center justify-between mb-6">
          <div className="space-y-2">
            <div className="h-6 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-40 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
            <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-48 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
          </div>
          <div className="w-24 h-9 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-md animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
        </div>
        <div className="space-y-4">
          {[...Array(3)]?.map((_, i) => (
            <div key={i} className="p-4 rounded-xl bg-muted animate-pulse">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-200 to-gray-300" />
                  <div className="space-y-2">
                    <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-32 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
                    <div className="h-3 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-24 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
                  </div>
                </div>
                <div className="w-16 h-6 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-lg animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
              </div>
              <div className="mb-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-32 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
                  <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-12 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
                </div>
                <div className="relative w-full h-2 bg-card rounded-full overflow-hidden">
                  <div className="absolute top-0 left-0 h-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full w-2/3 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl p-4 md:p-6 shadow-elevation-2 hover:shadow-elevation-3 transition-all duration-300">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">Financial Goals</h2>
          <p className="text-sm text-muted-foreground">Track your savings targets</p>
        </div>
        <Button variant="outline" iconName="Plus" iconPosition="left" size="sm">
          New Goal
        </Button>
      </div>
      <div className="space-y-4">
        {goals?.map((goal) => {
          const progress = calculateProgress(goal?.current, goal?.target);
          const daysRemaining = calculateDaysRemaining(goal?.targetDate);
          const isOnTrack = progress >= (100 - (daysRemaining / goal?.totalDays) * 100);

          return (
            <div key={goal?.id} className="p-4 rounded-xl bg-muted hover:bg-muted/80 transition-all duration-300 hover:shadow-elevation-2 hover:scale-[1.01] cursor-pointer group animate-fade-in">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-3" style={{ backgroundColor: `${goal?.color}15` }}>
                    <Icon name={goal?.icon} size={20} color={goal?.color} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground transition-colors duration-200 group-hover:text-primary">{goal?.name}</h3>
                    <p className="text-xs text-muted-foreground">{goal?.category}</p>
                  </div>
                </div>
                <div className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-all duration-200 hover:scale-110 ${isOnTrack ? 'bg-success/10' : 'bg-warning/10'}`}>
                  <Icon name={isOnTrack ? 'TrendingUp' : 'AlertCircle'} size={14} color={isOnTrack ? 'var(--color-success)' : 'var(--color-warning)'} />
                  <span className={`text-xs font-medium ${isOnTrack ? 'text-success' : 'text-warning'}`}>
                    {isOnTrack ? 'On Track' : 'Behind'}
                  </span>
                </div>
              </div>
              <div className="mb-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">
                    ${goal?.current?.toLocaleString()} of ${goal?.target?.toLocaleString()}
                  </span>
                  <span className="text-sm font-medium text-primary">{progress?.toFixed(0)}%</span>
                </div>
                <div className="relative w-full h-2 bg-card rounded-full overflow-hidden">
                  <div
                    className="absolute top-0 left-0 h-full rounded-full transition-all duration-500 ease-out"
                    style={{
                      width: `${progress}%`,
                      backgroundColor: goal?.color
                    }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <Icon name="Calendar" size={14} color="var(--color-muted-foreground)" />
                  <span className="text-xs text-muted-foreground">{daysRemaining} days left</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="TrendingUp" size={14} color="var(--color-success)" />
                  <span className="text-xs text-success">+${goal?.monthlyContribution}/mo</span>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-border">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Completion Probability</span>
                  <span className="text-xs font-semibold text-foreground">{goal?.probability}%</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GoalTracker;